<template>
  <div ref="clinicRef" class="clinic-container clinic-3d">
    <div class="loader" v-if="loadingProgress < 100">
      <span class="loadertext">{{ loadingProgressText }}</span>
    </div>
    <ClinicInside
      class="clinicinside"
      :version="modelVersion"
      :style="{ visibility: isClinicVisible ? 'visible' : 'hidden' }"
      :started="isActive"
      :init-timeout="0"
      :controls-enabled="true"
      :options="clinic3dOptions"
      :init-callback="clinicInitCallback"
    />
    <VideoPlayer />
  </div>
</template>

<script setup lang="ts">
import { Vector3 } from 'three';
import ClinicInside from '~/components/clinic-3d/ClinicInside.vue';
import VideoPlayer from '~/components/clinic-3d/VideoPlayer.vue';
import { useHeaderIntersection } from '~/composables/useHeaderIntersection';
import { useViewportSize } from '~/utils/useViewportSize';

import type { ContentClinic3dFragment } from '#gql';
import type { Clinic3dOptions, POCVersion } from '~/clinic-inside/useClinic3d';

const clinicRef = ref<null | HTMLElement>(null);
const isClinicVisible = ref(false);
const isActive = ref(false);
const loadingProgress = ref<number>(0);

const { isVs } = useViewportSize();

// Use different models based on viewport size
const modelVersion = computed<POCVersion>(() => {
  return isVs.value ? 'mobile' : 'desktop';
});

const clinic3dOptions: Clinic3dOptions = {
  debug: false,
  textOffset: new Vector3(0, -0.32, 0),
  parallaxEnabled: true,
  parallaxStrength: 0.5,
  maxFPS: 60,
  resolution: 1,
};

const loadingProgressText = computed(() => {
  return loadingProgress.value === 0
    ? 'Loading'
    : `${Math.round(loadingProgress.value)}%`;
});

defineProps<{
  data: ContentClinic3dFragment;
}>();

useHeaderIntersection(clinicRef, {
  elementStart: '10%',
  elementEnd: 'bottom',
});

onMounted(() => {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-experience', 'loading');
  }

  setWindowProgress();
});

onBeforeUnmount(() => {
  // Reset loading progress when component unmounts
  if (typeof window !== 'undefined' && (window as any).Clinic3d) {
    (window as any).Clinic3d.LoadingProgress.GlbModel = 0;
  }
});

function clinicInitCallback() {
  if (loadingProgress.value >= 100) {
    setClinicInitialized();
  } else {
    watch(loadingProgress, (newValue) => {
      if (newValue >= 100) {
        setClinicInitialized();
      }
    });
  }
}

function setClinicInitialized() {
  isClinicVisible.value = true;
  isActive.value = true;
}

function setWindowProgress() {
  let tickRequest: number | null;

  tick();

  function tick() {
    if (typeof window !== 'undefined' && (window as any).Clinic3d) {
      loadingProgress.value = (window as any).Clinic3d.LoadingProgress.GlbModel;
    }

    if (loadingProgress.value >= 100) {
      stop();
    } else if (typeof window !== 'undefined') {
      tickRequest = window.requestAnimationFrame(tick);
    }
  }

  function stop() {
    if (typeof window !== 'undefined' && typeof tickRequest === 'number') {
      window.cancelAnimationFrame(tickRequest);
      tickRequest = null;
      document.documentElement.setAttribute('data-experience', 'loaded');
    }
  }
}
</script>

<style lang="scss" scoped>
.clinic-container {
  padding: 0 !important;
  height: 1100vh;
  overflow: unset;
  position: relative;
  z-index: 0 !important;
}

.clinic-container > .loader {
  position: absolute;
  inset: 0;
  z-index: 10;
}

.clinic-container > .loader > .loadertext {
  position: sticky;
  top: 0;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
